import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Phone } from 'lucide-react';
import { callbackAPI } from '../lib/api';
import DdgCaptcha from './DdgCaptcha';
import { DDG_CAPTCHA_REQUIRED_MESSAGE } from '../lib/ddgCaptcha';
import PersonalDataConsent from './PersonalDataConsent';
import MarketingConsent from './MarketingConsent';
import {
  COMPANY_WEBSITE_LABEL,
  QUESTION_LABEL,
  WORK_EMAIL_LABEL,
  companyWebsiteSchema,
  optionalQuestionSchema,
  workEmailSchema,
} from '../lib/formFieldSchemas';

const phoneRegex = /^(\+7|7|8)?[\s-]?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2}$/;

const buildCallbackFormSchema = (requireCompanyWebsite, showContactFields) =>
  z
    .object({
      phone: z.string().optional().or(z.literal('')),
      email: showContactFields
        ? workEmailSchema
        : z.string().optional().or(z.literal('')),
      companyWebsite: requireCompanyWebsite
        ? companyWebsiteSchema
        : z.string().optional().or(z.literal('')),
      question: optionalQuestionSchema,
      consent: z.boolean().refine((val) => val === true, {
        message: 'Необходимо согласие на обработку персональных данных',
      }),
      marketingConsent: z.boolean().optional(),
    })
    .refine(
      (data) => {
        if (showContactFields) {
          return Boolean(data.email?.trim());
        }
        const hasPhone = data.phone && phoneRegex.test(data.phone);
        return Boolean(hasPhone);
      },
      {
        message: showContactFields
          ? 'Укажите рабочую почту'
          : 'Введите номер телефона',
        path: showContactFields ? ['email'] : ['phone'],
      },
    );

const CallbackForm = ({
  open,
  onOpenChange,
  privacyPolicyLink: _privacyPolicyLink,
  calculatorData = null,
  title = 'Заказать звонок',
  description = 'Оставьте свой номер телефона, и наш специалист свяжется с вами в ближайшее время',
  submitLabel = 'Заказать звонок',
  extraInfoLabel = 'Детали заказа:',
  requireInn = false,
  requireCompanyWebsite = requireInn,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const showContactFields =
    title.includes('Подобрать решение') || title.includes('Свяжитесь со мной');

  const callbackFormSchema = useMemo(
    () => buildCallbackFormSchema(requireCompanyWebsite, showContactFields),
    [requireCompanyWebsite, showContactFields],
  );

  const defaultValues = useMemo(
    () => ({
      phone: '',
      email: '',
      companyWebsite: '',
      question: '',
      consent: false,
      marketingConsent: false,
    }),
    [],
  );

  const form = useForm({
    resolver: zodResolver(callbackFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    } else {
      setCaptchaToken('');
    }
  }, [open, form, defaultValues]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const hasPhone = data.phone && phoneRegex.test(data.phone);
      const contact = showContactFields
        ? data.email.trim()
        : hasPhone
          ? data.phone.trim()
          : data.email?.trim();
      if (!captchaToken) {
        alert(DDG_CAPTCHA_REQUIRED_MESSAGE);
        return;
      }
      await callbackAPI.createCallback(contact, window.location.href, {
        phone: hasPhone ? data.phone.trim() : undefined,
        email: showContactFields || data.email?.trim() ? data.email?.trim() : undefined,
        companyWebsite: data.companyWebsite?.trim() || undefined,
        question: data.question?.trim() || undefined,
        consent: data.consent,
        contactType: showContactFields || !hasPhone ? 'email' : 'phone',
        marketingConsent: data.marketingConsent,
        captchaToken,
      });

      alert('Спасибо! Мы свяжемся с вами в ближайшее время.');
      form.reset(defaultValues);
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting callback form:', error);
      alert(error.message || 'Произошла ошибка. Попробуйте позже.');
      captchaRef.current?.reset();
      setCaptchaToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-accent" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {calculatorData && (
          <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-sm mb-2">{extraInfoLabel}</h4>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div>Количество пользователей: {calculatorData.userCount}</div>
              {calculatorData.selectedServices.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Дополнительные услуги:</div>
                  <ul className="list-disc list-inside ml-2">
                    {calculatorData.selectedServices.map((service, idx) => (
                      <li key={idx}>{service.name}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="font-semibold text-foreground mt-2 pt-2 border-t border-border/50">
                Итого: {calculatorData.totalPrice.toLocaleString('ru-RU')} ₽
              </div>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {showContactFields ? (
              <>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{WORK_EMAIL_LABEL} *</FormLabel>
                      <FormControl>
                        <Input placeholder="name@company.ru" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Телефон</FormLabel>
                      <FormControl>
                        <Input placeholder="+7 (999) 123-45-67" type="tel" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            ) : (
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Номер телефона</FormLabel>
                    <FormControl>
                      <Input placeholder="+7 (999) 123-45-67" type="tel" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {requireCompanyWebsite && (
              <FormField
                control={form.control}
                name="companyWebsite"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{COMPANY_WEBSITE_LABEL} *</FormLabel>
                    <FormControl>
                      <Input placeholder="company.ru" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{QUESTION_LABEL}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Кратко опишите задачу или вопрос"
                      className="min-h-[88px] resize-y"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="consent"
              render={({ field }) => (
                <FormItem>
                  <PersonalDataConsent
                    id="callback-pd-consent"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    error={form.formState.errors.consent?.message}
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="marketingConsent"
              render={({ field }) => (
                <FormItem>
                  <MarketingConsent
                    id="callback-marketing-consent"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormItem>
              )}
            />

            <DdgCaptcha ref={captchaRef} onToken={setCaptchaToken} />

            <Button
              type="submit"
              className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
              disabled={isSubmitting || !captchaToken}
            >
              {isSubmitting ? 'Отправка...' : submitLabel}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CallbackForm;
